import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Autocomplete,
} from "@mui/material";
import { AdditionalPerson, Employee } from "@/types";
import AdditionalPeopleEditor from "./AdditionalPeopleEditor";

interface AddMembershipFormProps {
  employees: Employee[];
  isLoadingEmployees: boolean;
  selectedEmployee: Employee | null;
  onEmployeeChange: (employee: Employee | null) => void;
  createMonth: string;
  defaultMonthLabel: string;
  createMonthOptions: string[];
  onCreateMonthChange: (month: string) => void;
  additionalPeople: AdditionalPerson[];
  onAddPerson: () => void;
  onRemovePerson: (index: number) => void;
  onPersonChange: (
    index: number,
    field: "name" | "surname" | "email",
    value: string,
  ) => void;
  onSubmit: (event: React.FormEvent) => void;
}

const AddMembershipForm: React.FC<AddMembershipFormProps> = ({
  employees,
  isLoadingEmployees,
  selectedEmployee,
  onEmployeeChange,
  createMonth,
  defaultMonthLabel,
  createMonthOptions,
  onCreateMonthChange,
  additionalPeople,
  onAddPerson,
  onRemovePerson,
  onPersonChange,
  onSubmit,
}) => (
  <Box
    sx={{
      flex: "1 1 0",
      minWidth: { xs: "100%", md: 300 },
      maxWidth: { xs: "100%", md: "none" },
    }}
  >
    <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <Typography variant="h6" gutterBottom>
          Add Fitpass Membership
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Choose an employee, optional additional people, and the month to
          activate their membership.
        </Typography>
        <Box
          component="form"
          onSubmit={onSubmit}
          sx={{ display: "flex", flexDirection: "column", gap: 2, flex: 1 }}
        >
          <Autocomplete
            options={employees}
            getOptionLabel={(option) =>
              `${option.firstName} ${option.lastName}`
            }
            value={selectedEmployee}
            onChange={(_, newValue) => onEmployeeChange(newValue)}
            disabled={isLoadingEmployees}
            sx={{ width: "100%" }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Employee"
                placeholder="Select an employee"
              />
            )}
          />
          <FormControl fullWidth>
            <InputLabel>Month</InputLabel>
            <Select
              value={createMonth || defaultMonthLabel}
              label="Month"
              onChange={(e) => onCreateMonthChange(e.target.value)}
            >
              {createMonthOptions.map((month) => (
                <MenuItem key={month} value={month}>
                  {month}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <AdditionalPeopleEditor
            people={additionalPeople}
            onAdd={onAddPerson}
            onRemove={onRemovePerson}
            onChange={onPersonChange}
          />

          <Button
            type="submit"
            variant="contained"
            color="primary"
            sx={{ mt: 1, alignSelf: "flex-start" }}
            disabled={!selectedEmployee?.id}
          >
            Add Membership
          </Button>
        </Box>
      </CardContent>
    </Card>
  </Box>
);

export default AddMembershipForm;
